
export enum View {
    // App Views
    LOGIN = 'LOGIN',
    REGISTER = 'REGISTER',
    RESET_PASSWORD = 'RESET_PASSWORD',
    PET_FEED = 'PET_FEED',
    PET_PROFILES = 'PET_PROFILES',
    PET_ADVICE = 'PET_ADVICE',
    USER_PROFILE = 'USER_PROFILE',
    FOUND_MODE = 'FOUND_MODE', // Nueva vista para quien escanea el QR
    FEATURES = 'FEATURES', // Nueva vista de Marketing/Features
    PRIVACY_POLICY = 'PRIVACY_POLICY', // Política de Privacidad — requerida para publicar en Google Play
    DELETE_ACCOUNT = 'DELETE_ACCOUNT', // Solicitud de eliminación de cuenta — requerido por Data Safety de Play
    SUBSCRIPTION = 'SUBSCRIPTION', // Nueva vista de Planes
    ACADEMY = 'ACADEMY', // Academia del Campeón - Gamificación
    VET_LANDING = 'VET_LANDING', // Marketing page for veterinarians
    LANDING = 'LANDING', // Main Web Welcome Page
    USER_HUB = 'USER_HUB', // Professional User Dashboard
    PET_ENVIRONMENT = 'PET_ENVIRONMENT', // Ringo-style Environment Dashboard
    COMMAND_CENTER = 'COMMAND_CENTER', // Bento-style Customizable Dashboard

    // Sub-views for Tutorials (Virtual Views)
    PET_MEDICAL = 'PET_MEDICAL',
    PET_CONSULTATION = 'PET_CONSULTATION',
    PET_ADVICE_CHAT = 'PET_ADVICE_CHAT',

    // NexusDB Admin Views
    DASHBOARD = 'DASHBOARD',
    SQL_BUILDER = 'SQL_BUILDER',
    SCHEMA_ARCHITECT = 'SCHEMA_ARCHITECT',
    SERVER_STATUS = 'SERVER_STATUS',
    BACKEND_GENERATOR = 'BACKEND_GENERATOR',
    API_TESTER = 'API_TESTER',
    FRONTEND_GENERATOR = 'FRONTEND_GENERATOR',
    ADMIN_PANEL = 'ADMIN_PANEL', // E-commerce Admin
    PRODUCT_DETAILS = 'PRODUCT_DETAILS', // E-commerce Product Detail
    MARKETPLACE = 'MARKETPLACE', // Client Marketplace / Store
    ROOT_PANEL = 'ROOT_PANEL', // Super Admin / Root Panel
    CHECKOUT = 'CHECKOUT', // E-commerce Checkout
    PLAYDATES = 'PLAYDATES', // Social Matching
    GPS_TRACKING = 'GPS_TRACKING', // Live Map
    COMMUNITY_ALERTS = 'COMMUNITY_ALERTS', // Lost & Found Feed
    VET_DASHBOARD = 'VET_DASHBOARD', // Dedicated Vet Portal
    BREEDER_DASHBOARD = 'BREEDER_DASHBOARD', // Criadero Profesional
    SHELTER_DASHBOARD = 'SHELTER_DASHBOARD', // Albergue / Rescate
    MAGIC_ADD = 'MAGIC_ADD', // Proximity Friend Addition
    PUBLIC_PROFILE = 'PUBLIC_PROFILE', // Perfil público de Albergue/Criadero
    BOOK_APPOINTMENT = 'BOOK_APPOINTMENT', // User Appointment Booking View
    TRACKER_ONBOARDING = 'TRACKER_ONBOARDING', // Vincular collar GPS (QR + BLE) a una mascota
    LOST_PETS_HALL = 'LOST_PETS_HALL', // Premium Lost Pets Environment
    NOTIFICATIONS = 'NOTIFICATIONS', // Personal User Notifications
    ADOPTION_REQUESTS = 'ADOPTION_REQUESTS', // Dueño particular ve quién quiere adoptar a su mascota
    PET_LIVE_TRACKING = 'PET_LIVE_TRACKING', // Owner sees live location of finder after QR scan
}

export interface ServerStat {
    name: string;
    value: number;
    total: number;
    unit: string;
}

export interface GeneratedSQL {
    sql: string;
    explanation: string;
}

export interface SchemaTable {
    tableName: string;
    columns: {
        name: string;
        type: string;
        constraints?: string;
    }[];
}

export interface LogEntry {
    id: string;
    timestamp: string;
    level: 'INFO' | 'WARN' | 'ERROR';
    message: string;
}

export interface Post {
    id: string;
    petId: string;
    petName: string;
    petAvatar: string;
    content: string;
    imageUrl?: string;
    likes: number;
    timestamp: string;
    isLiked?: boolean;
}

export enum PetType {
    DOG = 'Perro',
    CAT = 'Gato',
    BIRD = 'Ave',
    RODENT = 'Roedor',
    FERRET = 'Hurón',
    OTHER = 'Otro'
}

export interface VaccinationEntry {
    id: string;
    month?: string;
    suggestedAction: string;
    dueDate?: string;
    status: 'pending' | 'completed' | 'overdue';
    location?: string;
    attachment?: string;
    registeredDate?: string;
}

export interface MedicationEntry {
    id: string;
    name: string;
    startDate?: string;
    timeStart?: string; // HH:mm
    frequencyHours: number;
    durationDays?: number;
    lastTaken?: string; // ISO timestamp
    nextDose?: string; // ISO timestamp or HH:mm
    remainingDoses: number;
    totalDoses?: number;
    status: 'active' | 'completed';
    type?: string;
}

export interface FileAttachment {
    name: string;
    url: string;
}

export interface HealthRecord {
    id: string;
    petId?: string; // Internal React state use
    pet_id?: string; // DB Sync
    type: string; // 'Control', 'Vacuna', 'Receta', 'Examen', etc.
    date: string;
    clinic: string;
    doctor?: string;
    diagnosis?: string;
    recommendation?: string;
    exams?: any[]; // JSON array of strings or objects
    prescriptionsFiles?: FileAttachment[];
    prescriptions?: any[]; // JSON array
    
    // New fields for Workspace 2.0
    episode_id?: string;
    is_episode_active?: boolean;
    next_due_date?: string;

    // Legacy/Compat fields (mapped to above)
    motivo?: string;
    fecha?: string;
    lugar?: string;
    veterinario?: string;
    notas?: string;
    archivos?: string[];
}

export interface PetProfile {
    id: string;
    name: string;
    type: PetType;
    species?: string; // Kept for backward compatibility with API responses
    breed?: string;
    birthDate?: string;
    imageUrl?: string;
    color?: string;
    gender?: string;
    weight?: string | number;
    energy?: number;

    // Medical & Health
    vaccinations: VaccinationEntry[];
    healthRecords: HealthRecord[];
    medications: MedicationEntry[];

    // Interview Data
    dailyFood?: string;
    rewardFood?: string;
    specialDiet?: string;
    foodAllergies?: string;
    medicalImportance?: string;
    likes?: string;
    dislikes?: string;
    favoriteToy?: string;
    favoritePlace?: string;
    tricks?: string;
    fears?: string;

    // IDs
    hasChip?: boolean;
    chipNumber?: string;
    hasIdCard?: boolean;
    hasNfc?: boolean;

    // Owner info
    ownerName?: string;
    ownerPhone?: string;
    address?: string;
}

export interface UserProfile {
    id?: string;
    name: string;
    email: string;
    phone: string;
    rut: string;
    country?: string;
    region: string;
    city: string;
    address: string;
    address_ref?: string; // Depto, Casa, Unidad
    imageUrl: string;
    password?: string; // Only for registration payload
    status?: string;

    // Plan & Roles
    role?: 'user' | 'admin' | 'root' | 'vet' | 'breeder' | 'shelter';
    // Accesos adicionales aprobados por admin (multi-portal)
    partner_roles?: ('vet' | 'shelter' | 'breeder')[];
    plan?: 'free' | 'premium' | 'family';

    // E-commerce & Wallet
    wallet_balance?: number; // Saldo disponible
    points?: number; // Puntos acumulados
    reward_tier?: 'Bronze' | 'Silver' | 'Gold' | 'Platinum'; // Nivel de recompensas
    total_purchases?: number; // Total gastado
    last_purchase_date?: string; // Última compra

    // AI Limits
    ai_usage_basic?: number;
    ai_usage_advanced?: number;

    // Security
    must_change_password?: boolean;

    // Privacy
    is_public_wall?: boolean;
}

export interface HospitalizationTreatment {
    id: number;
    hospitalization_id: number;
    treatment_type: 'medication' | 'fluid' | 'vitals' | 'diet' | 'cleaning';
    name: string;
    frequency_hours: number;
    price: number;
    created_at: string;
}

export interface HospitalizationLog {
    id: number;
    hospitalization_id: number;
    treatment_id: number | null;
    action_type: 'treatment_done' | 'vitals_check' | 'note' | 'status_change';
    details: string;
    staff_name: string;
    billed: number;
    logged_at: string;
    treatment_name?: string;
}

export interface Hospitalization {
    id: number;
    vet_id: string;
    pet_id: number;
    cage_label: string;
    gravity: 'low' | 'medium' | 'critical';
    reason: string;
    status: 'admitted' | 'discharged';
    admission_date: string;
    discharge_date: string | null;
    initial_notes: string;
    pet_name: string;
    pet_breed: string;
    pet_species: string;
    pet_image_url: string | null;
    pet_birth_date: string | null;
    pet_gender: string | null;
    owner_name: string | null;
    owner_email: string | null;
    owner_phone: string | null;
    treatments: HospitalizationTreatment[];
    logs: HospitalizationLog[];
}
